import { get, isEmpty, sortBy } from 'lodash'
import { createSelector } from 'reselect'

import {
  APPOINTMENT_CALLBACK_FIELDS_MODE,
  APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM
} from 'src/scenes/Companies/scenes/CompaniesSearch/constants'
import { addAllValue, mapDataHierarchy } from 'src/utils/DataTrees'
import {
  addAllValue as addAllValueV5,
  convertDataFields
} from 'src/utils/v5/DataTrees'

const mapBillingCodes = (codes = []) => {
  const mappedCodes = []

  sortBy(codes, ({ type }) => type).forEach(
    ({ uuid, code: billingCode, description, children, isDefault }) => {
      if (!isEmpty(children)) {
        const code = {
          key: uuid,
          title: `${billingCode} - ${description} / ${children
            .map((child) => `${child.code} - ${child.description}`)
            .join(' / ')}`,
          label: `${billingCode} - ${description} / ${children
            .map((child) => `${child.code} - ${child.description}`)
            .join(' / ')}`,
          value: uuid,
          isDefault
        }

        mappedCodes.push(code)
      } else {
        const code = {
          key: uuid,
          title: `${billingCode} - ${description}`,
          label: `${billingCode} - ${description}`,
          value: uuid,
          isDefault
        }

        mappedCodes.push(code)
      }
    }
  )

  return mappedCodes
}

const companiesAdministrating = (state) =>
  get(state, 'companiesAdministrating', {})

const getAppointmentCallbackFieldsMode = (company = {}) => {
  const appointmentCallbackFieldsMode =
    company?.[APPOINTMENT_CALLBACK_FIELDS_MODE]

  if (typeof appointmentCallbackFieldsMode === 'number') {
    return appointmentCallbackFieldsMode
  }

  return company?.isAppointmentCallbackFieldsRequired
    ? APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.REQUIRED
    : APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.HIDDEN
}

export const companiesList$ = createSelector(
  companiesAdministrating,
  ({ companiesList }) => companiesList
)

export const activeCompany$ = createSelector(
  companiesAdministrating,
  ({ activeCompany }) => activeCompany
)

export const activeCompanyUuid$ = createSelector(
  activeCompany$,
  (activeCompany = {}) => activeCompany?.uuid || null
)

export const isAddEditModalOpen$ = createSelector(
  companiesAdministrating,
  ({ isAddEditModalOpen }) => isAddEditModalOpen
)

export const editableCompany$ = createSelector(
  companiesAdministrating,
  ({ editableCompany }) => editableCompany
)

export const companyUsers$ = createSelector(
  companiesAdministrating,
  ({ companyUsers }) => companyUsers
)

export const companyDivision$ = createSelector(
  companiesAdministrating,
  ({ companyDivision }) => companyDivision
)

export const companyDivisionByCompany$ = createSelector(
  companiesAdministrating,
  ({ companyDivisionByCompany }) => companyDivisionByCompany
)

export const companyDivisionHierarchy$ = createSelector(
  companyDivision$,
  (companyDivision = []) => mapDataHierarchy(companyDivision)
)

export const companyDivisionHierarchyV5$ = createSelector(
  companyDivision$,
  (companyDivision = []) => convertDataFields(companyDivision, 'children')
)

export const companyDivisionHierarchyByCompany$ = createSelector(
  companyDivisionByCompany$,
  (companyDivisionByCompany = []) => mapDataHierarchy(companyDivisionByCompany)
)

export const companyLanguages$ = createSelector(
  companiesAdministrating,
  ({ companyLanguages }) =>
    companyLanguages.map((el) => ({
      ...el,
      key: el.uuid,
      value: el.uuid,
      label: el.refValue
    }))
)

export const userRolePermissions$ = createSelector(
  companiesAdministrating,
  ({ userRolePermissions }) => userRolePermissions
)

export const companyLicensesList$ = createSelector(
  companiesAdministrating,
  ({ companyLicensesList }) => companyLicensesList
)

export const licensesList$ = createSelector(
  companiesAdministrating,
  ({ licensesList }) => licensesList
)

export const companyModalLoading$ = createSelector(
  companiesAdministrating,
  ({ companyModalLoading }) => companyModalLoading
)

export const userRoles$ = createSelector(
  companiesAdministrating,
  ({ userRoles }) => userRoles
)

export const userRolesDropdown$ = createSelector(userRoles$, (userRoles = []) =>
  userRoles.map(({ uuid, name }) => ({
    key: uuid,
    value: uuid,
    title: name
  }))
)

export const userRolesDropdownAntd5$ = createSelector(
  userRoles$,
  (userRoles = []) =>
    userRoles.map(({ uuid, name }) => ({
      key: uuid,
      value: uuid,
      label: name
    }))
)

export const caseManagersByDivision$ = createSelector(
  companiesAdministrating,
  ({ caseManagersByDivision }) =>
    caseManagersByDivision.map((manager) => ({
      ...manager,
      value: manager.uuid,
      label: manager.fullName,
      title: manager.fullName,
      disabled: !manager.enabled
    }))
)

export const caseManagersByDivisionWithAll$ = createSelector(
  caseManagersByDivision$,
  (caseManagersByDivision = []) =>
    addAllValue(caseManagersByDivision, true, 'All Managers')
)

export const companyCaseManagers$ = createSelector(
  companiesAdministrating,
  ({ companyCaseManagers = [] }) => companyCaseManagers
)

export const activeCompanyProviders$ = createSelector(
  activeCompany$,
  ({ providers = [] }) =>
    providers.map(({ uuid, name }) => ({
      key: uuid,
      label: name,
      value: uuid
    }))
)

export const activeCompanyAppointmentCallbackFieldsMode$ = createSelector(
  activeCompany$,
  (activeCompany = {}) => getAppointmentCallbackFieldsMode(activeCompany)
)

export const activeCompanyIsAppointmentCallbackFieldsVisible$ = createSelector(
  activeCompanyAppointmentCallbackFieldsMode$,
  (appointmentCallbackFieldsMode) =>
    appointmentCallbackFieldsMode !==
    APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.HIDDEN
)

export const activeCompanyIsAppointmentCallbackFieldsRequired$ = createSelector(
  activeCompanyIsAppointmentCallbackFieldsVisible$,
  (isAppointmentCallbackFieldsVisible) => isAppointmentCallbackFieldsVisible
)

export const activeCompanyIsAppointmentCallbackFieldsRequiredMode$ =
  createSelector(
    activeCompany$,
    (activeCompany = {}) =>
      getAppointmentCallbackFieldsMode(activeCompany) ===
      APPOINTMENT_CALLBACK_FIELDS_MODE_ENUM.REQUIRED
  )

export const activeCompanyProvidersWithAll$ = createSelector(
  activeCompanyProviders$,
  (activeCompanyProviders = []) =>
    addAllValueV5({ list: activeCompanyProviders })
)

export const editableUser$ = createSelector(
  companiesAdministrating,
  ({ editableUser = {} }) => editableUser
)

export const companyUserModalClinicians$ = createSelector(
  companiesAdministrating,
  ({ companyUserModalClinicians = [] }) =>
    companyUserModalClinicians.map((item) => ({
      ...item,
      disabled: !item.enabled
    }))
)

export const companyUserModalCliniciansWithAll$ = createSelector(
  companyUserModalClinicians$,
  (companyUserModalClinicians = []) =>
    addAllValue(companyUserModalClinicians, true)
)

export const adminBillingPrograms$ = createSelector(
  companiesAdministrating,
  ({ adminBillingPrograms = [] }) => adminBillingPrograms
)

export const adminBillingProgramsDropdown$ = createSelector(
  adminBillingPrograms$,
  (adminBillingPrograms = []) => mapBillingCodes(adminBillingPrograms)
)

export const companyGoalCategories$ = createSelector(
  companiesAdministrating,
  ({ companyGoalCategories = [] }) =>
    companyGoalCategories.map((e) => ({
      ...e,
      label: e.title,
      key: e.uuid,
      value: e.uuid
    }))
)

export const companyGoals$ = createSelector(
  companiesAdministrating,
  ({ companyGoals = [] }) =>
    companyGoals
      .filter((goal) => !goal?.content.includes('[Inactive]'))
      .map((e) => ({
        key: e.uuid,
        value: e.uuid,
        title: e?.content,
        label: e?.content
      }))
)

export const rpmCompanyGoals$ = createSelector(
  companiesAdministrating,
  ({ rpmCompanyGoals = [] }) =>
    rpmCompanyGoals
      .filter((goal) => !goal?.content.includes('[Inactive]'))
      .map((el) => ({
        key: el.uuid,
        value: el.uuid,
        label: el?.content
      }))
)
